// zod schemas per component. Keep in sync with the table in CLAUDE.md.
import { z } from "zod";
export const COMPONENT_NAMES = [
    "callout",
    "checklist",
    "table",
    "mermaid",
    "decision",
    "timeline",
    "cards",
    "stats",
    "chart",
    "columns",
];
export const TEXT_COMPONENTS = ["callout", "checklist", "table", "mermaid"];
export function isComponentName(name) {
    return COMPONENT_NAMES.includes(name);
}
/** Text components take their content in the fence body; structured ones take YAML. */
export function isTextComponent(name) {
    return TEXT_COMPONENTS.includes(name);
}
/** Prefix used when assigning ids (`<prefix><n>`). Headings use `h`. */
export const ID_PREFIX = {
    heading: "h",
    callout: "c",
    checklist: "k",
    table: "tb",
    mermaid: "m",
    decision: "d",
    timeline: "tl",
    cards: "cd",
    stats: "s",
    chart: "ch",
    columns: "col",
};
export const TONES = ["info", "tip", "warning", "danger", "note"];
export const toneSchema = z.enum(TONES);
/**
 * Stats read as good / bad / neutral rather than info / warning. Models also guess
 * words like `success` or `down`; accept those as aliases (the guide lists them from this map), and keep the callout
 * tones working for notes written before this existed.
 */
export const STAT_TONES = ["good", "bad", "neutral", ...TONES];
export const STAT_TONE_ALIASES = {
    success: "good",
    positive: "good",
    up: "good",
    negative: "bad",
    failure: "bad",
    down: "bad",
    flat: "neutral",
};
/** The aliases as guide prose: "`success`, `positive` … and `flat`". */
const STAT_TONE_ALIAS_LIST = (() => {
    const names = Object.keys(STAT_TONE_ALIASES).map((a) => `\`${a}\``);
    return `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
})();
export const statToneSchema = z.preprocess((v) => (typeof v === "string" && v in STAT_TONE_ALIASES ? STAT_TONE_ALIASES[v] : v), z.enum(STAT_TONES));
const str = z.string();
const nonEmpty = z.string().min(1, "must not be empty");
/** YAML turns unquoted numbers/dates into non-strings; accept scalars and stringify them. */
const scalarString = z.union([z.string(), z.number(), z.boolean()]).transform((v) => String(v));
export const calloutSchema = z
    .object({
    tone: toneSchema.default("info").describe("Accent colour and icon; info is the quiet default."),
    title: str.optional().describe("Optional bold line above the text."),
})
    .strict();
export const checklistSchema = z
    .object({
    items: z.array(z.object({ text: str, checked: z.boolean() }).strict()).min(1, "needs at least one task item"),
})
    .strict();
export const tableSchema = z
    .object({
    header: z.array(str).min(1),
    rows: z.array(z.array(str)),
})
    .strict();
export const mermaidSchema = z.object({}).strict();
export const decisionSchema = z
    .object({
    question: nonEmpty.describe("The question that was decided."),
    options: z
        .array(z
        .object({
        id: scalarString.optional().describe("Stable handle for `chosen`; unique within the options."),
        label: scalarString.describe("What the option is called."),
        note: scalarString.optional().describe("One line about the option."),
    })
        .strict())
        .min(1, "needs at least one option")
        .describe("The choices being weighed."),
    chosen: scalarString.optional().describe("The chosen option's `id`, or its label when the option has no `id`."),
    rationale: str.optional().describe("Why this option won."),
})
    .strict()
    .superRefine((v, ctx) => {
    const ids = v.options.map((o) => o.id).filter((x) => x !== undefined);
    if (new Set(ids).size !== ids.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["options"], message: "option ids must be unique" });
    }
    if (v.chosen !== undefined) {
        const hit = v.options.some((o) => o.id === v.chosen) || v.options.some((o) => o.label === v.chosen);
        if (!hit) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["chosen"],
                message: `"${v.chosen}" matches no option id or label (options: ${v.options
                    .map((o) => o.id ?? o.label)
                    .join(", ")})`,
            });
        }
    }
});
export const timelineSchema = z
    .object({
    items: z
        .array(z
        .object({
        date: scalarString.optional().describe("Day, month or phase label."),
        title: scalarString.describe("What happened or is planned."),
        body: str.optional().describe("Optional Markdown detail under the title."),
        status: z
            .enum(["done", "current", "upcoming"])
            .optional()
            .describe("Where the entry stands; highlights the current one."),
    })
        .strict())
        .min(1, "needs at least one item")
        .describe("Entries in order."),
})
    .strict();
export const cardsSchema = z
    .object({
    columns: z.union([z.literal(2), z.literal(3)]).optional().describe("Grid width."),
    items: z
        .array(z
        .object({
        title: scalarString.describe("Card heading."),
        body: str.optional().describe("Optional Markdown text under the title."),
        tone: toneSchema.optional().describe("Accent colour, as in callout."),
    })
        .strict())
        .min(1, "needs at least one item")
        .describe("One entry per card."),
})
    .strict();
export const statsSchema = z
    .object({
    items: z
        .array(z
        .object({
        label: scalarString.describe("What the number is."),
        value: scalarString.describe("The number itself."),
        delta: scalarString.optional().describe("Optional change line, e.g. `+12 this week`."),
        tone: statToneSchema
            .optional()
            .describe(`Colour of the figure; ${STAT_TONE_ALIAS_LIST} are accepted as aliases.`),
    })
        .strict())
        .min(1, "needs at least one item")
        .describe("One entry per figure."),
})
    .strict();
export const chartSchema = z
    .object({
    type: z.enum(["bar", "line", "area", "pie"]).describe("Chart shape."),
    x: z.array(scalarString).min(1).describe("Category labels along the axis."),
    series: z
        .array(z
        .object({
        name: scalarString.describe("Legend label."),
        data: z.array(z.number()).describe("Exactly one number per `x` value."),
    })
        .strict())
        .min(1, "needs at least one series")
        .describe("One entry per line, bar group or pie slice."),
    title: str.optional().describe("Optional heading above the chart."),
})
    .strict()
    .superRefine((v, ctx) => {
    v.series.forEach((s, i) => {
        if (s.data.length !== v.x.length) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["series", i, "data"],
                message: `series "${s.name}" has ${s.data.length} values but x has ${v.x.length}`,
            });
        }
    });
});
export const columnsSchema = z
    .object({
    count: z.union([z.literal(2), z.literal(3)]).optional().describe("Number of columns."),
})
    .strict();
export const COMPONENT_SCHEMAS = {
    callout: calloutSchema,
    checklist: checklistSchema,
    table: tableSchema,
    mermaid: mermaidSchema,
    decision: decisionSchema,
    timeline: timelineSchema,
    cards: cardsSchema,
    stats: statsSchema,
    chart: chartSchema,
    columns: columnsSchema,
};
const BT = "```";
const BT4 = "````";
export const COMPONENT_META = {
    callout: {
        name: "callout",
        title: "Callout",
        summary: "Warnings, tips, takeaways",
        body: "text",
        bodyNote: "Body is Markdown.",
        examples: [
            `${BT}callout tone=warning title="Heads up"
Claude and ChatGPT need dynamic client registration.
${BT}`,
        ],
        template: {
            snippet: `${BT}callout tone=tip title="Worth remembering"\nSomething short and useful.\n${BT}`,
            select: "Something short and useful.",
        },
    },
    checklist: {
        name: "checklist",
        title: "Checklist",
        summary: "Next steps (GFM task list)",
        body: "text",
        bodyNote: "Body is a GFM task list (`- [ ]` / `- [x]`); items are parsed from it.",
        derivedProps: ["items"],
        examples: [
            `${BT}checklist
- [x] Pick OAuth approach
- [ ] Test DCR with Claude
${BT}`,
        ],
        template: {
            snippet: `${BT}checklist\n- [ ] First step\n- [ ] Second step\n${BT}`,
            select: "First step",
        },
    },
    table: {
        name: "table",
        title: "Table",
        summary: "Comparisons (GFM table)",
        body: "text",
        bodyNote: "Body is a GFM table; header and rows are parsed from it.",
        derivedProps: ["header", "rows"],
        examples: [
            `${BT}table
| Option | Cost | Notes |
|---|---|---|
| Authentik | high | check DCR |
| Built-in | low | less work |
${BT}`,
        ],
        template: {
            snippet: `${BT}table\n| Option | Cost | Notes |\n|---|---|---|\n| One | low | |\n| Two | high | |\n${BT}`,
            select: "Option",
        },
    },
    mermaid: {
        name: "mermaid",
        title: "Mermaid",
        summary: "Flowcharts, sequences, architecture",
        body: "text",
        bodyNote: "Body is Mermaid source.",
        examples: [
            `${BT}mermaid
flowchart LR
  Claude --> MCP --> Postgres
${BT}`,
        ],
        template: {
            snippet: `${BT}mermaid\nflowchart LR\n  A --> B --> C\n${BT}`,
            select: "A --> B --> C",
        },
    },
    decision: {
        name: "decision",
        title: "Decision",
        summary: "A choice and why",
        body: "yaml",
        bodyNote: "Body is YAML.",
        hint: "`chosen` must match an option `id`, or its `label` when the option has no `id`.",
        examples: [
            `${BT}decision
question: How do clients authenticate?
options:
  - id: authentik
    label: Authentik
    note: check DCR support first
  - id: builtin
    label: Built-in OAuth
    note: less work for one user
chosen: builtin
rationale: One user, no IdP to run.
${BT}`,
        ],
        template: {
            snippet: `${BT}decision\nquestion: What are we deciding?\noptions:\n  - id: a\n    label: Option A\n    note: why it might be right\n  - id: b\n    label: Option B\nchosen: a\nrationale: One line on why.\n${BT}`,
            select: "What are we deciding?",
        },
    },
    timeline: {
        name: "timeline",
        title: "Timeline",
        summary: "Plans, phases, history",
        body: "yaml",
        bodyNote: "Body is YAML.",
        examples: [
            `${BT}timeline
items:
  - date: 2026-11-03
    title: OAuth spike
    status: done
  - title: Threads
    body: thread ids on writes, recent, timeline view
    status: current
  - title: Public sharing polish
    status: upcoming
${BT}`,
        ],
        template: {
            snippet: `${BT}timeline\nitems:\n  - date: 2026-11-01\n    title: Done already\n    status: done\n  - title: Happening now\n    body: a line about it\n    status: current\n  - title: Later\n    status: upcoming\n${BT}`,
            select: "Done already",
        },
    },
    cards: {
        name: "cards",
        title: "Cards",
        summary: "Options side by side",
        body: "yaml",
        bodyNote: "Body is YAML.",
        examples: [
            `${BT}cards
columns: 2
items:
  - title: Postgres FTS
    body: Fast, built in, no extra service.
  - title: pgvector
    body: Semantic search; needs embeddings.
    tone: tip
${BT}`,
        ],
        template: {
            snippet: `${BT}cards\ncolumns: 2\nitems:\n  - title: First\n    body: What it is.\n  - title: Second\n    body: What it is.\n    tone: tip\n${BT}`,
            select: "First",
        },
    },
    stats: {
        name: "stats",
        title: "Stats",
        summary: "Headline numbers",
        body: "yaml",
        bodyNote: "Body is YAML.",
        hint: `${STAT_TONE_ALIAS_LIST} are accepted as aliases of \`good\`/\`bad\`/\`neutral\`.`,
        examples: [
            `${BT}stats
items:
  - label: Notes
    value: 128
    delta: +12 this week
    tone: good
  - label: Errors
    value: 3
    delta: -2 today
    tone: bad
  - label: Uptime
    value: 99.9%
    tone: neutral
${BT}`,
        ],
        template: {
            snippet: `${BT}stats\nitems:\n  - label: Notes\n    value: 128\n    delta: +12 this week\n  - label: Uptime\n    value: 99.9%\n${BT}`,
            select: "Notes",
        },
    },
    chart: {
        name: "chart",
        title: "Chart",
        summary: "Data you supply",
        body: "yaml",
        bodyNote: "Body is YAML.",
        hint: "Every series has one number per `x` entry.",
        examples: [
            `${BT}chart
type: bar
title: Writes per client
x: [Mon, Tue, Wed]
series:
  - name: Claude
    data: [4, 7, 3]
${BT}`,
        ],
        template: {
            snippet: `${BT}chart\ntype: bar\ntitle: Writes per day\nx: [Mon, Tue, Wed]\nseries:\n  - name: Claude\n    data: [4, 7, 3]\n${BT}`,
            select: "Writes per day",
        },
    },
    columns: {
        name: "columns",
        title: "Columns",
        summary: "Blocks next to each other",
        body: "nested",
        bodyNote: "Four backticks outside, three inside; the body is Markdown with other blocks.",
        hint: "`columns` cannot contain `columns`.",
        examples: [
            `${BT4}columns count=2
${BT}callout tone=tip
Left column.
${BT}

${BT}callout
Right column.
${BT}
${BT4}`,
        ],
        template: {
            snippet: `${BT4}columns count=2\n${BT}callout tone=tip\nLeft column.\n${BT}\n\n${BT}callout\nRight column.\n${BT}\n${BT4}`,
            select: "Left column.",
        },
    },
};
function pathOf(issue) {
    return issue.path.length ? issue.path.map((p) => (typeof p === "number" ? `[${p}]` : p)).join(".").replace(/\.\[/g, "[") : "";
}
function describeIssue(issue) {
    const path = pathOf(issue);
    const at = path ? `${path}: ` : "";
    switch (issue.code) {
        case "invalid_enum_value":
            return `${at}must be one of ${issue.options.map(String).join("|")} (got ${JSON.stringify(issue.received)})`;
        case "invalid_type":
            if (issue.received === "undefined")
                return `${path || "value"} is required`;
            return `${at}expected ${issue.expected}, got ${issue.received}`;
        case "unrecognized_keys":
            return `${at}unknown key${issue.keys.length > 1 ? "s" : ""} ${issue.keys.map((k) => `"${k}"`).join(", ")}`;
        case "too_small":
            if (issue.type === "array")
                return `${at}needs at least ${issue.minimum} item${issue.minimum === 1 ? "" : "s"}`;
            if (issue.type === "string")
                return `${at}must not be empty`;
            return `${at}${issue.message}`;
        case "invalid_literal":
            return `${at}must be ${JSON.stringify(issue.expected)}`;
        case "invalid_union": {
            const firsts = issue.unionErrors.map((e) => e.issues[0]).filter((x) => !!x);
            if (firsts.length && firsts.every((f) => f.code === "invalid_type" && f.received === "undefined")) {
                return `${path || "value"} is required`;
            }
            const expected = [...new Set(firsts.map((f) => (f.code === "invalid_type" ? f.expected : f.code === "invalid_literal" ? JSON.stringify(f.expected) : "")).filter(Boolean))];
            return `${at}expected ${expected.join("|") || "a valid value"}`;
        }
        default:
            return `${at}${issue.message}`;
    }
}
/** Validate props for a component. Error messages list the failing paths, one per line. */
export function validateComponent(name, props) {
    if (!isComponentName(name)) {
        return { ok: false, error: `unknown component "${name}" (known: ${COMPONENT_NAMES.join(", ")})` };
    }
    const result = COMPONENT_SCHEMAS[name].safeParse(props);
    if (result.success)
        return { ok: true, props: result.data };
    const seen = new Set();
    const lines = [];
    for (const issue of result.error.issues) {
        const line = describeIssue(issue);
        if (!seen.has(line)) {
            seen.add(line);
            lines.push(line);
        }
    }
    return { ok: false, error: `${name}: ${lines.join("; ")}` };
}
//# sourceMappingURL=schemas.js.map